import { useMemo } from 'react';
import { AutocompleteSelect } from '@/Components/shared/AutocompleteSelect';

interface Society {
  uuid: string;
  name: string;
  code?: string;
  city?: string;
}

interface SocietySelectorProps {
  value: string;
  onChange: (societyId: string) => void;
  error?: string;
  disabled?: boolean;
  allowedSocietyIds?: string[];
  size?: 'default' | 'sm';
}

export const SocietySelector: React.FC<SocietySelectorProps> = ({
  value,
  onChange,
  error,
  disabled = false,
  allowedSocietyIds,
  size = 'default',
}) => {
  // Restriction au périmètre autorisé (société principale + ancêtres) : on filtre
  // CÔTÉ SERVEUR via le param `societyIds` — l'API ne renvoie alors que ce
  // périmètre. Auparavant le filtrage se faisait côté client (filterFn) sur un
  // endpoint paginé, ce qui obligeait à parcourir TOUTES les pages de /societies
  // pour faire remonter les quelques sociétés autorisées (boucle / requêtes en
  // rafale au focus).
  const additionalParams = useMemo(
    () =>
      allowedSocietyIds && allowedSocietyIds.length > 0
        ? { societyIds: allowedSocietyIds.join(',') }
        : undefined,
    [allowedSocietyIds],
  );

  return (
    <div>
      <AutocompleteSelect<Society>
        apiEndpoint="/societies"
        searchParam="nameFilter"
        getOptionLabel={(s) => s.name + (s.code ? ` (${s.code})` : '')}
        getOptionValue={(s) => s.uuid}
        value={value || null}
        onChange={(uuid) => onChange(uuid || '')}
        label="Société principale"
        required
        placeholder="Rechercher une société..."
        disabled={disabled}
        size={size}
        additionalParams={additionalParams}
        filterActive
        renderOption={(society) => (
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className="tw-flex-1">
              <div className="tw-font-medium">{society.name}</div>
              {(society.code || society.city) && (
                <div className="tw-text-xs tw-text-gray-500">
                  {society.code && `Code: ${society.code}`}
                  {society.code && society.city && ' • '}
                  {society.city}
                </div>
              )}
            </div>
          </div>
        )}
      />
      {error && <p className="tw-mt-1 tw-text-sm tw-text-red-600">{error}</p>}
    </div>
  );
};
